import { useContext, useState } from 'react';
import { Container } from 'src/components/ui';
import AppContext from '../context/AppContext';
import { AuthContext } from '../context/AuthContext';
import { RouterContextProvider } from '../context/RouterContext';
import Sidebar from './Sidebar';
import NavMenu from './NavMenu';
import Loader from './Loader';
import './WorkpageShellSkeleton.css';
import { Trans } from '@lingui/react/macro';

const CHART_BARS = [
  { x: 156, height: 10 },
  { x: 168, height: 18 },
  { x: 180, height: 28 },
  { x: 192, height: 14 },
  { x: 204, height: 22 },
];

const TABLE_COLUMN_STARTS = [64, 110, 156, 192];
const TABLE_ROW_CENTERS = [93, 103.5, 114.5, 126];
const TABLE_CELL_WIDTHS = [
  [22, 20, 16, 18],
  [30, 24, 20, 14],
  [24, 30, 14, 20],
  [28, 18, 22, 16],
];

const TABLE_CELLS = TABLE_ROW_CENTERS.flatMap((y, rowIndex) =>
  TABLE_COLUMN_STARTS.map((x, columnIndex) => ({
    x,
    y,
    width: TABLE_CELL_WIDTHS[rowIndex][columnIndex],
    isHeader: rowIndex === 0,
  })),
);

/**
 * Blueprint-style illustration shown in place of a fake workpage table: the
 * real workpage load always ends in a full-screen Loader takeover anyway, so
 * mimicking a table here never actually gets "filled in" by real content —
 * an honest loading illustration reads better than a skeleton nothing will
 * resolve into. Rects use `pathLength={100}` so `stroke-dasharray`/
 * `stroke-dashoffset` can animate a 0-100 draw-in regardless of each rect's
 * actual perimeter.
 */
export function BuildingBlueprint() {
  return (
    <svg viewBox="0 0 240 150" className="blueprint-illustration" aria-hidden="true">
      <defs>
        <pattern id="blueprint-grid" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="240" height="150" fill="url(#blueprint-grid)" className="blueprint-grid" />

      {/* App frame */}
      <rect x="10" y="10" width="220" height="130" rx="4" pathLength={100} className="blueprint-line blueprint-frame" />
      {/* Sidebar */}
      <rect x="10" y="10" width="40" height="130" pathLength={100} className="blueprint-line blueprint-sidebar" />
      {/* Navbar */}
      <rect x="50" y="10" width="180" height="18" pathLength={100} className="blueprint-line blueprint-navbar" />
      {/* Content panels */}
      <rect x="58" y="36" width="80" height="44" rx="2" pathLength={100} className="blueprint-line blueprint-panel-1" />
      <rect x="146" y="36" width="76" height="44" rx="2" pathLength={100} className="blueprint-line blueprint-panel-2" />
      <rect x="58" y="88" width="164" height="44" rx="2" pathLength={100} className="blueprint-line blueprint-panel-3" />

      {/* Panel 1: pie chart */}
      <circle cx="98" cy="58" r="15" pathLength={100} className="blueprint-line blueprint-pie-ring" />
      <path d="M 98 58 L 98 43" pathLength={100} className="blueprint-line blueprint-pie-slice-1" />
      <path d="M 98 58 L 111 65.5" pathLength={100} className="blueprint-line blueprint-pie-slice-2" />
      <path d="M 98 58 L 85 65.5" pathLength={100} className="blueprint-line blueprint-pie-slice-3" />

      {/* Panel 2: bar chart */}
      <path d="M 153 72 L 215 72" pathLength={100} className="blueprint-line blueprint-bar-axis" />
      {CHART_BARS.map((bar, index) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={72 - bar.height}
          width="7"
          height={bar.height}
          pathLength={100}
          className="blueprint-line blueprint-bar"
          style={{ animationDelay: `${-1.9 - index * 0.1}s` }}
        />
      ))}

      {/* Panel 3: data table — header rule, row rules, column dividers, then cell "text" */}
      <path d="M 58 98 L 222 98 M 58 109 L 222 109 M 58 120 L 222 120" pathLength={100} className="blueprint-line blueprint-table-rows" />
      <path d="M 104 88 L 104 132 M 150 88 L 150 132 M 186 88 L 186 132" pathLength={100} className="blueprint-line blueprint-table-cols" />
      {TABLE_CELLS.map(({ x, y, width, isHeader }) => (
        <path
          key={`${x}-${y}`}
          d={`M ${x} ${y} L ${x + width} ${y}`}
          pathLength={100}
          className={`blueprint-line blueprint-table-cell${isHeader ? ' blueprint-table-cell--header' : ''}`}
        />
      ))}
    </svg>
  );
}

/**
 * Loading shell shown while UWEContext is still fetching workpages/app-options.
 * Mounts the REAL Sidebar/NavMenu (so the logo, collapse state, and the
 * settings/FAQ/user-dropdown footer stay live and functional) and fakes only
 * the pieces that actually depend on workpage data: the sidebar's nav-tab
 * list (via Sidebar's own built-in `isLoading` skeleton rows) and the main
 * content, shown as a blueprint illustration of the app being drafted rather
 * than a fake data table (see BuildingBlueprint above).
 */
function WorkpageShellSkeleton() {
  const auth = useContext(AuthContext);
  const { sidebarExpanded } = useContext(AppContext);
  const { user } = auth ?? {};

  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('internal-layout-sidebar') === 'closed');

  function toggleSidebar() {
    const newValue = isCollapsed ? 'open' : 'closed';
    setIsCollapsed(newValue === 'closed');
    localStorage.setItem('internal-layout-sidebar', newValue);
  }

  if (!user) return <Loader fullscreen />;

  return (
    <RouterContextProvider>
      <div className={`app-layout ${sidebarExpanded ? 'has-sidebar-tab-open' : ''}`}>
        <Sidebar auth={auth} user={user} isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} isLoading />
        <Container className="mui-main-content" fluid>
          <NavMenu isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
          <div className="app-layout-internal">
            {/* Most workpages render with ui:asCard=false (WorkTray's "flat" layout) — a
                title bar over content, no card chrome — so that's the shape we fake here. */}
            <div className="workpage">
              <div className="workpage-widget from-col-1-size-4">
                <div className="worktray worktray--flat">
                  <div className="worktray-flat-scroll">
                    <div className="workpage-building-loader">
                      <BuildingBlueprint />
                      <div>
                        <p className="workpage-building-title">
                          <Trans>Building your OCIF experience...</Trans>
                        </p>
                        <p className="workpage-building-subtitle">
                          <Trans>Setting up your workpages</Trans>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </RouterContextProvider>
  );
}

export default WorkpageShellSkeleton;
